import React, { useCallback, useEffect, useState } from 'react';
import './PowerTraceabilityTable.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const token = () => localStorage.getItem('token') || sessionStorage.getItem('token');

/** Sólo uso interno: nunca se monta en pantallas de cliente. */
export default function PowerTraceabilityTable({ poderId, endpoint, recipient, fetcher = window.fetch }) {
  const base = `${API_URL}${endpoint}`;
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [channel, setChannel] = useState('shared');
  const [commentRows, setCommentRows] = useState([]);
  const [commentOptions, setCommentOptions] = useState({ channels: ['shared'], recipients: [] });
  const [notifyUserIds, setNotifyUserIds] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editedText, setEditedText] = useState('');
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');

  const refresh = useCallback(async (before = null) => {
    if (!poderId) return;
    setLoading(true);
    setError('');
    try {
      const url = `${base}/trace${before ? `?before=${encodeURIComponent(before)}` : ''}`;
      const response = await fetcher(url, { headers: { 'x-auth-token': token() } });
      const data = await response.json();
      if (!response.ok) throw new Error(data.msg || 'No se pudo consultar la trazabilidad.');
      setRows(old => before ? [...data.items, ...old].filter((item, index, items) => items.findIndex(other => other.id === item.id) === index) : data.items);
      setHasMore(Boolean(data.hasMore));
      setPage(data.nextBefore);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [base, fetcher, poderId]);

  useEffect(() => { setRows([]); setPage(null); refresh(); }, [refresh]);
  const loadComments = useCallback(async () => {
    try {
      const response = await fetcher(`${base}/comments?channel=${encodeURIComponent(channel)}`, { headers: { 'x-auth-token': token() } });
      const result = await response.json();
      if (!response.ok) throw new Error(result.msg || 'No se pudieron cargar comentarios.');
      setCommentRows(result.items || []);
    } catch (e) { setError(e.message); }
  }, [base, channel, fetcher]);
  useEffect(() => { loadComments(); }, [loadComments]);
  useEffect(() => {
    fetcher(`${base}/comments/options`, { headers: { 'x-auth-token': token() } })
      .then(async response => { const data = await response.json(); if (!response.ok) throw new Error(data.msg); setCommentOptions(data); })
      .catch(error => setError(error.message || 'No se pudieron consultar los destinatarios.'));
  }, [base, fetcher]);

  const send = async event => {
    event.preventDefault();
    if (!message.trim() || sending) return;
    setSending(true);
    setFeedback('');
    setError('');
    try {
      const response = await fetcher(`${base}/comments`, {
        method: 'POST', headers: { 'x-auth-token': token(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: message.trim(), channel, notifyUserIds }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.msg || 'No se pudo registrar el comentario.');
      setMessage(''); setNotifyUserIds([]); loadComments();
      setFeedback(data.queued ? 'Comentario registrado y notificación programada.' : (data.warning || 'Comentario registrado.'));
      refresh();
    } catch (e) { setError(e.message); }
    finally { setSending(false); }
  };

  const saveCommentEdit = async () => {
    if (!editing || !editedText.trim()) return;
    setSending(true); setError('');
    try {
      const response = await fetcher(`${base}/comments/${editing}`, { method: 'PATCH',
        headers: { 'x-auth-token': token(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: editedText.trim() }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.msg || 'No se pudo editar el comentario.');
      setEditing(null); setEditedText(''); loadComments(); refresh();
    } catch (e) { setError(e.message); } finally { setSending(false); }
  };
  return (
    <section className="power-trace" aria-label="Trazabilidad de la orden de servicio">
      <div className="power-trace-header">
        <div><h3>Tabla de estatus e historial</h3><p>Eventos registrados por fecha y hora efectiva · Monterrey (24 horas).</p></div>
        <button type="button" onClick={() => refresh()} disabled={loading}>Actualizar</button>
      </div>
      {error && <p role="alert" className="power-trace-error">{error}</p>}
      <div className="power-trace-scroll" role="region" aria-label="Historial cronológico" tabIndex={0}>
        <table>
          <thead><tr><th>Estatus / hito</th><th>Rol responsable</th><th>Nombre del responsable</th><th>Fecha</th><th>Hora</th><th>Acción / condición</th></tr></thead>
          <tbody>
            {rows.map(row => <tr key={row.id}>
              <td>{row.hito}</td><td>{row.role}</td><td>{row.name}</td>
              <td>{row.fecha}</td><td>{row.hora}</td><td>{row.details || row.action}</td>
            </tr>)}
            {!rows.length && <tr><td colSpan={6}>{loading ? 'Cargando historial…' : 'Sin eventos registrados para este expediente.'}</td></tr>}
          </tbody>
        </table>
      </div>
      {hasMore && <button type="button" onClick={() => refresh(page)} disabled={loading}>Ver eventos anteriores</button>}
      <section className="power-trace-conversations">
        <h4>Conversaciones del expediente</h4>
        <label>Espacio de comunicación
          <select value={channel} onChange={e => { setChannel(e.target.value); setNotifyUserIds([]); }}>
            {(commentOptions.channels || ['shared']).map(key => <option value={key} key={key}>{key === 'shared' ? 'Compartido Wisby–Notaría' : key === 'wisby' ? 'Interno Wisby' : 'Interno Notaría'}</option>)}
          </select>
        </label>
        <div className="power-trace-chat">
          {commentRows.map(comment => <article key={comment._id}>
            <strong>{comment.authorName}</strong> <small>{new Date(comment.createdAt).toLocaleString('es-MX')}{comment.lastEditedAt ? ' · Editado' : ''}</small>
            {editing === comment._id ? <><textarea value={editedText} onChange={event => setEditedText(event.target.value)} maxLength={3000} /><button type="button" disabled={sending || !editedText.trim()} onClick={saveCommentEdit}>Guardar corrección</button><button type="button" onClick={() => setEditing(null)}>Cancelar</button></> :
              <><p>{comment.text}</p>{comment.canEdit && <button type="button" onClick={() => { setEditing(comment._id); setEditedText(comment.text); }}>Editar mi comentario</button>}</>}
          </article>)}
          {!commentRows.length && <p>No hay comentarios en esta conversación.</p>}
        </div>
      </section>
      <form onSubmit={send} className="power-trace-comment">
        <label htmlFor={`power-comment-${poderId}`}>Nuevo comentario · {channel === 'shared' ? 'Compartido Wisby–Notaría' : 'Solo equipo ' + (channel === 'wisby' ? 'Wisby' : 'Notaría')}</label>
        <fieldset><legend>{channel === 'shared' ? 'Notificar por correo a' : 'Mencionar a un integrante por correo (opcional)'}</legend>
          {(commentOptions.recipients || []).filter(person => channel === 'shared' || person.team === channel).map(person =>
            <label key={person._id}><input type="checkbox" checked={notifyUserIds.includes(person._id)} onChange={event =>
              setNotifyUserIds(old => event.target.checked ? [...old, person._id] : old.filter(id => id !== person._id))} />{person.name}</label>)}
        </fieldset>
        <textarea id={`power-comment-${poderId}`} value={message} maxLength={3000} rows={3} onChange={e => setMessage(e.target.value)} placeholder="Escribe el comentario que se enviará por correo y quedará en el historial…" />
        <div><small>{message.length}/3000</small><button disabled={sending || !message.trim()} type="submit">{sending ? 'Registrando…' : 'Registrar y notificar'}</button></div>
        {feedback && <p role="status">{feedback}</p>}
      </form>
    </section>
  );
}
